/**
 * The countries Mucka's phone fields accept, and the pure functions that
 * assemble and take apart a number.
 *
 * DIRECTIVE-FREE ON PURPOSE (anti-pattern #18). These are pure helpers and
 * `PhoneField` is a `"use client"` module; anything exported from there is a
 * client-reference proxy that throws the moment a server component calls it.
 * Nothing on the server reads them today, but the signup route is the obvious
 * next caller and that is exactly how MUCKA-WEB-4S happened.
 *
 * WHY THIS EXISTS. Until now the country code was a hard-coded `+44` constant
 * rendered as a STATIC chip with no picker, and `buildFullPhone` took a single
 * raw string. A German mobile typed as `0151 23456789` therefore came out as
 * `+4415123456789`: not rejected, not flagged — silently stored as a UK number
 * that will never ring. `validatePhone` only counted digits, so nothing
 * anywhere disagreed.
 */

export type CountryCode = 'GB' | 'DE';

export interface PhoneCountry {
  code: CountryCode;
  /** E.164 calling code, with the plus. */
  dial: string;
  flag: string;
  label: string;
  /** What a local number looks like here, trunk zero and all. */
  placeholder: string;
}

/**
 * UK first — it is the overwhelming majority and the default for anyone who
 * never opens the picker. Germany is here because an investor needed a real
 * seat; adding a third is a row in this array plus a test case, deliberately
 * nothing else.
 */
export const PHONE_COUNTRIES: PhoneCountry[] = [
  { code: 'GB', dial: '+44', flag: '🇬🇧', label: 'United Kingdom', placeholder: '07123 456789' },
  { code: 'DE', dial: '+49', flag: '🇩🇪', label: 'Germany', placeholder: '0151 23456789' },
];

export const DEFAULT_COUNTRY: PhoneCountry = PHONE_COUNTRIES[0];

/**
 * What the field holds: the chosen country plus the LOCAL part as typed.
 *
 * One object rather than two arguments, so it is not possible to call
 * `buildFullPhone` with the number and forget the dial code — which is
 * anti-pattern #28's shape (one record, two places to name every field) and
 * would put the old bug straight back for whichever call site was missed.
 */
export interface PhoneValue {
  country: CountryCode;
  local: string;
}

export const EMPTY_PHONE: PhoneValue = { country: DEFAULT_COUNTRY.code, local: '' };

export function countryOf(code: CountryCode): PhoneCountry {
  return PHONE_COUNTRIES.find((c) => c.code === code) ?? DEFAULT_COUNTRY;
}

/** Digits of a dial code, without the plus. `+44` → `44`. */
function dialDigits(c: PhoneCountry): string {
  return c.dial.slice(1);
}

/**
 * Did they paste a full international number? Returns the country it belongs
 * to and the digits after the code, or null if this is an ordinary local one.
 *
 * Handles both spellings of the international prefix (`+49…`, `0049…`). A
 * number carrying a code we do not offer resolves to null and is treated as
 * local, because silently keeping a country that is not in the picker would
 * leave the chip disagreeing with what gets sent (anti-pattern #33: the
 * control has to show what the customer will actually get).
 */
export function detectPastedCountry(
  raw: string,
): { country: CountryCode; subscriber: string } | null {
  const trimmed = raw.trim();
  const hasPlus = trimmed.startsWith('+');
  let digits = trimmed.replace(/\D/g, '');
  const hasZeroZero = !hasPlus && digits.startsWith('00');
  if (!hasPlus && !hasZeroZero) return null;
  if (hasZeroZero) digits = digits.slice(2);

  // Longest code first, so a future +1 never shadows a +1xxx.
  const match = [...PHONE_COUNTRIES]
    .sort((a, b) => dialDigits(b).length - dialDigits(a).length)
    .find((c) => digits.startsWith(dialDigits(c)));
  if (!match) return null;
  return { country: match.code, subscriber: digits.slice(dialDigits(match).length) };
}

/**
 * Normalise what the user typed into the pair the field should now show.
 *
 * Called on every keystroke by `PhoneField`, so pasting `+49 151 2345678`
 * while the chip says 🇬🇧 MOVES the chip to 🇩🇪 rather than producing
 * `+4449…`. The old single-country version guarded this by blanket-stripping
 * a leading `44`, which cannot work once there is more than one code.
 */
export function reconcileTyped(value: PhoneValue): PhoneValue {
  const pasted = detectPastedCountry(value.local);
  if (pasted) return { country: pasted.country, local: pasted.subscriber };
  return value;
}

/**
 * `{ country, local }` → canonical E.164.
 *
 * Strips the trunk zero (UK and Germany both write local numbers with one) and
 * a redundant copy of the selected country's own dial code, so a prefilled or
 * half-pasted `44` is never doubled against the chip.
 */
export function buildFullPhone(value: PhoneValue): string {
  const country = countryOf(value.country);
  const pasted = detectPastedCountry(value.local);
  if (pasted) return `${countryOf(pasted.country).dial}${pasted.subscriber.replace(/^0+/, '')}`;

  let digits = value.local.replace(/\D/g, '');
  const cc = dialDigits(country);
  if (digits.startsWith(cc)) digits = digits.slice(cc.length);
  digits = digits.replace(/^0+/, '');
  return `${country.dial}${digits}`;
}

/**
 * E.164 → the pair the editor should open on.
 *
 * Replaces the old `toLocalDraft`, which assumed `44` and returned the raw
 * E.164 unchanged for anything else — so a German number on file would have
 * been re-submitted with `+49…` sitting inside the local field.
 *
 * An unrecognised code falls back to the DEFAULT country with the number left
 * whole. That is the honest answer for the ~100% of rows written before this
 * existed, and it shows the user something they can correct rather than a
 * confidently wrong country.
 */
export function splitE164(e164: string): PhoneValue {
  if (!e164) return EMPTY_PHONE;
  const pasted = detectPastedCountry(e164.startsWith('+') ? e164 : `+${e164.replace(/\D/g, '')}`);
  if (pasted) return { country: pasted.country, local: `0${pasted.subscriber}` };
  return { country: DEFAULT_COUNTRY.code, local: e164.replace(/\D/g, '') };
}

/**
 * Pretty-print an E.164 number for display beside a "is this right?" prompt.
 *
 * Grouping is per country because the shapes genuinely differ; anything that
 * does not match the expected length is returned verbatim rather than grouped
 * wrongly, since a mis-grouped number reads as a different number.
 */
export function formatDisplayPhone(e164: string): string {
  const digits = e164.replace(/\D/g, '');
  if (digits.startsWith('44') && digits.length === 12) {
    return `+44 ${digits.slice(2, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
  }
  if (digits.startsWith('49') && digits.length >= 11 && digits.length <= 13) {
    return `+49 ${digits.slice(2, 5)} ${digits.slice(5)}`;
  }
  return e164;
}

/**
 * A LOOSE sanity check, and deliberately no more than that.
 *
 * It counts digits. It does not know which UK prefixes are allocated or that
 * German subscriber numbers vary from six digits to eleven, and pretending
 * otherwise would reject real customers to catch typos that pairing catches
 * anyway — a wrong number fails at the WhatsApp code, loudly, with the person
 * still on the screen.
 *
 * The UK bounds are unchanged from the single-country version on purpose: this
 * change exists to let a German number through, not to start turning UK ones
 * away. The single exception is a UK MOBILE of the wrong length (see
 * `isShortOrLongUkMobile`), which is a fact rather than a guess.
 */
export function validatePhone(value: PhoneValue): string | null {
  const pasted = detectPastedCountry(value.local);
  const country = pasted ? pasted.country : value.country;
  const digits = (pasted ? pasted.subscriber : value.local).replace(/\D/g, '').replace(/^0+/, '');
  if (digits.length === 0) return 'Enter a phone number.';
  if (digits.length < 6) return 'That phone number looks too short.';
  if (digits.length > 14) return 'That phone number looks too long.';
  if (country === 'GB' && isShortOrLongUkMobile(digits)) return UK_MOBILE_LENGTH_ERROR;
  return null;
}

export const UK_MOBILE_LENGTH_ERROR = 'A UK mobile number is 11 digits, starting 07.';

/**
 * The one UK rule this file is sure of: every UK mobile is 07 plus nine more
 * digits, so its national part (the digits after the 0 or the +44) is exactly
 * ten long and starts with 7. It deliberately says nothing about landlines,
 * whose lengths genuinely vary.
 *
 * Without it, `+44 7123 5677` signed up on prod (September 2026) — four digits
 * short, and the WhatsApp step it goes on to need can never reach it.
 *
 * `national` is the UK number with the country code and trunk zero already
 * stripped. Shared by the signup form and the signup route so the two cannot
 * disagree about what a mobile is.
 */
export function isShortOrLongUkMobile(national: string): boolean {
  return national.startsWith('7') && national.length !== 10;
}
